import { useCallback } from 'react';
import type { ApolloError } from 'apollo-client';
import { AbortError } from '@atlassian/jira-business-error-handling';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
	useExperienceAbort,
} from '@atlassian/jira-business-experience-tracking';
import type { ChildrenQueryData } from '@atlassian/jira-business-list-handlers';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	type FetchError,
	type ValidationError,
	isValidationError,
	isClientFetchError,
	isHttpClientErrorResponse,
} from '@atlassian/jira-fetch';
import { useFlagService } from '@atlassian/jira-flags';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useStartDateField } from '@atlassian/jira-router-resources-business-configuration';
import type { OnChartItemUpdate } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/types';
import { SCHEDULE_ITEM_EXPERIENCE } from '../../common/constants';
import type {
	TimelineViewIssuesReturn,
	TimelineViewIssue,
	UpdateItemsInCache,
	UpdateIssueField,
} from '../../services/get-timeline-view-issues/types';
import { useScheduleItem } from '../../services/schedule-items';
import { useTimelineUI } from '../timeline-ui';
import { useLockIssue } from '../timeline-updater';
import {
	START_DATE_MISSING_FLAG,
	SCHEDULE_ITEM_FAIL_FLAG,
	SCHEDULE_ITEM_NOT_FOUND,
	DESCENDANTS_UPDATE_BATCH_SIZE,
} from './constants';
import { shouldScheduleChildrenIssues, getIssuesBulkDatesUpdate, toISOString } from './utils';

type Props = {
	issuesMap: Map<string, TimelineViewIssue>;
	updateItemsInCache: UpdateItemsInCache;
	childrenQueryData: ChildrenQueryData<TimelineViewIssuesReturn>;
};

type UpdateItemsInCacheList = {
	bulkUpdates: UpdateIssueField[];
	originalValues: UpdateIssueField[];
}[];

export const useUpdateItem = ({ issuesMap, updateItemsInCache, childrenQueryData }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagService();
	const lockIssue = useLockIssue();
	const [{ timelineDuration }] = useTimelineUI();
	const { onScheduleItem, onScheduleItems } = useScheduleItem();
	const { data: startDateField } = useStartDateField();

	const startExperience = useExperienceStart(SCHEDULE_ITEM_EXPERIENCE);
	const markExperienceAborted = useExperienceAbort(SCHEDULE_ITEM_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(SCHEDULE_ITEM_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(SCHEDULE_ITEM_EXPERIENCE);

	// Callback for the result of a drag and drop operation on an item
	const onItemUpdate: OnChartItemUpdate = useCallback(
		(id, param) => {
			const issue = issuesMap.get(id);
			if (!issue || !param.dates) {
				return;
			}
			const { dates } = param;

			const startDate = dates?.startDate != null ? toISOString(dates.startDate) : null;
			const dueDate = dates?.dueDate != null ? toISOString(dates.dueDate) : null;

			if (!startDate && !dueDate) {
				// no dates? no problem: do nothing!
				return;
			}

			const issueStartDate = issue.startDate != null ? toISOString(issue.startDate) : null;
			const issueDueDate = issue.dueDate != null ? toISOString(issue.dueDate) : null;

			// temporarily lock the issue to prevent updates coming in from realtime updates
			lockIssue(Number(id));

			const updateItemsInCacheList: UpdateItemsInCacheList = [];

			if (ff('jwm.visual-hierarchy-milestone-1')) {
				updateItemsInCacheList.push({
					bulkUpdates: [{ id, fields: { startDate, dueDate } }],
					originalValues: [{ id, fields: { startDate: issueStartDate, dueDate: issueDueDate } }],
				});
			} else {
				updateItemsInCache([{ id, fields: { startDate, dueDate } }]);
			}

			let childrenOriginalValues: UpdateIssueField[] = [];
			const shouldScheduleChildren = shouldScheduleChildrenIssues({
				issue,
				modifiedDates: dates,
			});

			if (shouldScheduleChildren) {
				if (ff('jwm.visual-hierarchy-milestone-1')) {
					const populateChildUpdates = (parentIssueId: string) => {
						const childrenOnCache = childrenQueryData[parentIssueId]?.data?.issues;

						if (childrenOnCache) {
							const { bulkUpdates, originalValues } = getIssuesBulkDatesUpdate({
								issue,
								issueChildren: childrenOnCache,
								dates,
								timelineDuration,
							});

							updateItemsInCacheList.push({ bulkUpdates, originalValues });
							childrenOnCache.forEach((child) => populateChildUpdates(child.issueId));
						}
					};

					populateChildUpdates(id);
				} else {
					const childrenOnCache = childrenQueryData[id]?.data?.issues;
					if (childrenOnCache) {
						const { bulkUpdates, originalValues } = getIssuesBulkDatesUpdate({
							issue,
							issueChildren: childrenOnCache,
							dates,
							timelineDuration,
						});

						if (bulkUpdates.length) {
							updateItemsInCache(bulkUpdates);
							childrenOriginalValues = originalValues;
						}
					}
				}
			}

			if (ff('jwm.visual-hierarchy-milestone-1')) {
				updateItemsInCacheList.forEach(({ bulkUpdates }) => {
					if (bulkUpdates.length) {
						updateItemsInCache(bulkUpdates);
					}
				});
			}

			const rollbackUpdates = () => {
				if (ff('jwm.visual-hierarchy-milestone-1')) {
					updateItemsInCacheList.forEach(({ originalValues }) => {
						if (originalValues.length) {
							updateItemsInCache(originalValues);
						}
					});
				} else {
					if (childrenOriginalValues.length) {
						updateItemsInCache(childrenOriginalValues);
					}
					updateItemsInCache([
						{
							id,
							fields: { startDate: issueStartDate, dueDate: issueDueDate },
						},
					]);
				}
			};

			// There are two kind of operations when the user set the start and due date on the same time
			// The user can SET new due and start date just clicking on the timeline
			// or de user can DRAG along and update both dates
			// For now we can detect the operation DRAG if the user has defined previusly start or due date
			// once we implement rollup dates, the user can drag just without any dates.
			// TODO: JET-3804 revisit condition once rollup dates is implemented.
			const isDragOperation =
				startDate != null && dueDate != null && (issueStartDate != null || issueDueDate != null);

			fireUIAnalytics(createAnalyticsEvent({}), 'itemDates updated', {
				startDate,
				dueDate,
				shouldScheduleChildren,
			});
			startExperience();

			if (ff('jwm.visual-hierarchy-milestone-1')) {
				// TODO: HRZ-917 replace the following once the backend api that updates the descendant items is implemented.
				// The following is a temporary workaround to update item and descendant issues
				let isParentItemSuccessfullyUpdated = false;

				const batchUpdateDescendants = async (descendants: UpdateIssueField[]) => {
					while (descendants.length > 0) {
						const batch = descendants.splice(0, DESCENDANTS_UPDATE_BATCH_SIZE);
						if (batch.length) {
							// eslint-disable-next-line no-await-in-loop -- items are already batched and concurrently processed. we need to wait for the batch to finish.
							const isSuccessful = await onScheduleItems(batch, isDragOperation);
							if (!isSuccessful) break;
						}
					}
				};

				const updateAllItems = async () => {
					const [_, ...descendantUpdateFields] = updateItemsInCacheList.flatMap(
						(item) => item.bulkUpdates,
					);

					const descendantIssueIds = descendantUpdateFields.map((field) => field.id);
					const success = await onScheduleItem(
						{
							issueId: id,
							isDragOperation,
							...dates,
						},
						descendantIssueIds,
					);

					if (success) {
						isParentItemSuccessfullyUpdated = true;
						await batchUpdateDescendants(descendantUpdateFields);
					}

					return success;
				};

				const onComplete = () => {
					markExperienceSuccess();

					fireTrackAnalytics(createAnalyticsEvent({}), 'scheduleItem success', {
						issueId: id,
						startDate,
						dueDate,
					});

					if (!isParentItemSuccessfullyUpdated) {
						showFlag(SCHEDULE_ITEM_FAIL_FLAG);
						rollbackUpdates();
					}
				};

				const onError = (err: Error | ValidationError | FetchError | ApolloError | AbortError) => {
					if (err instanceof AbortError) {
						markExperienceAborted(err.message || 'scheduleItem aborted');
						fireTrackAnalytics(createAnalyticsEvent({}), 'scheduleItem aborted', {
							error: err,
							startDate,
							dueDate,
						});

						return;
					}

					if ('statusCode' in err && err.statusCode === 404) {
						showFlag(SCHEDULE_ITEM_NOT_FOUND);
						return;
					}

					// Exclude client fetch, validation and 4xx errors from experience fail events reporting
					if (isClientFetchError(err) || isValidationError(err) || isHttpClientErrorResponse(err)) {
						markExperienceSuccess();
					} else {
						markExperienceFailed('schedule item failed', err);
					}

					fireTrackAnalytics(createAnalyticsEvent({}), 'scheduleItem failed', {
						error: err,
						startDate,
						dueDate,
					});

					showFlag(SCHEDULE_ITEM_FAIL_FLAG);

					if (ff('jwm-schedule-timeline-update-all-descendants-on-drag-operation_v39wp')) {
						rollbackUpdates();
					} else if (!isParentItemSuccessfullyUpdated) {
						rollbackUpdates();
					}
				};

				const finalise = () => {
					if (startDate != null && !startDateField) {
						showFlag(START_DATE_MISSING_FLAG);
					}
				};

				if (ff('jwm-schedule-timeline-update-all-descendants-on-drag-operation_v39wp')) {
					onScheduleItem({
						issueId: id,
						isDragOperation,
						...dates,
					})
						.then((success) => {
							if (success === true) {
								isParentItemSuccessfullyUpdated = true;
							}
							onComplete();
						})
						.catch(onError)
						.finally(finalise);
				} else {
					updateAllItems().then(onComplete).catch(onError).finally(finalise);
				}
			} else {
				onScheduleItem({
					issueId: id,
					isDragOperation,
					...dates,
				})
					.then(() => {
						markExperienceSuccess();
						fireTrackAnalytics(createAnalyticsEvent({}), 'scheduleItem success', {
							issueId: id,
							startDate,
							dueDate,
						});
					})
					.catch((err) => {
						if (err?.statusCode === 404) {
							showFlag(SCHEDULE_ITEM_NOT_FOUND);
							return;
						}

						// Exclude client fetch, validation and 4xx errors from experience fail events reporting
						if (
							err &&
							(isClientFetchError(err) || isValidationError(err) || isHttpClientErrorResponse(err))
						) {
							markExperienceSuccess();
						} else {
							markExperienceFailed('schedule item failed', err);
						}
						fireTrackAnalytics(createAnalyticsEvent({}), 'scheduleItem failed', {
							error: err,
							startDate,
							dueDate,
						});
						showFlag(SCHEDULE_ITEM_FAIL_FLAG);
						rollbackUpdates();
					})
					.finally(() => {
						if (startDate != null && !startDateField) {
							showFlag(START_DATE_MISSING_FLAG);
						}
					});
			}
		},
		[
			issuesMap,
			lockIssue,
			createAnalyticsEvent,
			startExperience,
			updateItemsInCache,
			childrenQueryData,
			timelineDuration,
			onScheduleItems,
			onScheduleItem,
			markExperienceSuccess,
			showFlag,
			markExperienceAborted,
			markExperienceFailed,
			startDateField,
		],
	);

	return { onItemUpdate };
};
